import { useState } from 'react';
import { formatUsd } from '@lumina/shared';
import { api } from '../lib/api';
import { formatDate, formatDay } from '../lib/format';

// Review / funds holds (workers/api/utils/fulfillment.js). A held order isn't
// sent to Gelato until it's approved (when over the review amount) and its
// Stripe payment has cleared (when the funds rule applies).

export interface HoldFields {
  review_required: number;
  approved_at: string | null;
  approved_by?: string | null;
  funds_required: number;
  funds_status: string | null;
  funds_available_on?: string | null;
  funds_checked_at?: string | null;
  released_early_by?: string | null;
  gelato_order_id: string | null;
  payment_status: string;
  status: string;
}

// Mirrors holdState() on the API.
export function holdState(o: HoldFields) {
  const active = !o.gelato_order_id && o.payment_status === 'paid' && o.status !== 'cancelled';
  const awaitingApproval = active && o.review_required === 1 && !o.approved_at;
  const awaitingFunds =
    active && o.funds_required === 1 && o.funds_status !== 'available' && !o.released_early_by;
  return { awaitingApproval, awaitingFunds, held: awaitingApproval || awaitingFunds };
}

/** Stacked under the order status in the orders list. */
export function HoldBadges({ order }: { order: HoldFields }) {
  const h = holdState(order);
  return (
    <>
      {h.awaitingApproval && <Badge color="#92400e" bg="#fef3c7">Needs approval</Badge>}
      {h.awaitingFunds && <Badge color="#1e40af" bg="#dbeafe">Awaiting funds</Badge>}
    </>
  );
}

function Badge({ color, bg, children }: { color: string; bg: string; children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'table',
        marginTop: 3,
        padding: '1px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        color,
        background: bg,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

interface HoldResult {
  dispatched?: boolean;
  funds_error?: string | null;
  error?: string;
}

/** Hold explanation and actions on the order page. Renders nothing for orders that were never held. */
export function OrderHoldPanel({
  order,
  orderUuid,
  total,
  onChanged,
}: {
  order: HoldFields;
  orderUuid: string;
  total: number;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  if (order.review_required !== 1 && order.funds_required !== 1) return null;
  const h = holdState(order);

  const run = async (label: string, path: string, confirmText?: string) => {
    if (confirmText && !confirm(confirmText)) return;
    setBusy(label);
    setMessage(null);
    try {
      const r = await api.post<HoldResult>(`/api/admin/orders/${orderUuid}/${path}`);
      setMessage(
        r.dispatched
          ? { text: 'Sent to Gelato.', error: false }
          : r.funds_error
            ? { text: `Couldn't check Stripe: ${r.funds_error}`, error: true }
            : { text: 'Updated.', error: false }
      );
      onChanged();
    } catch (err) {
      setMessage({ text: err instanceof Error ? err.message : 'Failed', error: true });
    } finally {
      setBusy(null);
    }
  };

  const fundsLine = (() => {
    if (order.funds_status === 'available') return { text: 'Cleared — available in Stripe', color: '#15803d' };
    if (order.funds_status === 'pending') {
      const on = order.funds_available_on ? formatDay(order.funds_available_on) : null;
      return { text: `Pending in Stripe${on ? ` · expected ${on}` : ''}`, color: '#1e40af' };
    }
    return { text: 'Not checked yet', color: '#6b7280' };
  })();

  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        borderRadius: 6,
        border: `1px solid ${h.held ? '#fcd34d' : '#e5e5e5'}`,
        background: h.held ? '#fffbeb' : '#fafafa',
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        {h.held ? 'Held from Gelato' : 'Review hold cleared'}
      </div>
      {order.review_required === 1 && (
        <div style={{ marginBottom: 4 }}>
          <span style={{ color: '#6b7280' }}>Approval: </span>
          {order.approved_at ? (
            <span style={{ color: '#15803d' }}>
              approved{order.approved_by ? ` by ${order.approved_by}` : ''} ·{' '}
              {formatDate(order.approved_at)}
            </span>
          ) : (
            <span style={{ color: '#92400e' }}>needed — {formatUsd(total)} is at or over the review amount</span>
          )}
        </div>
      )}
      {order.funds_required === 1 && (
        <div style={{ marginBottom: 4 }}>
          <span style={{ color: '#6b7280' }}>Payment: </span>
          <span style={{ color: fundsLine.color }}>{fundsLine.text}</span>
          {order.released_early_by && (
            <span style={{ color: '#92400e' }}> · released early by {order.released_early_by}</span>
          )}
          {order.funds_checked_at && (
            <div style={{ fontSize: 11, color: '#9ca3af' }}>
              Checked {formatDate(order.funds_checked_at)} · rechecked hourly
            </div>
          )}
        </div>
      )}
      {h.held && (
        <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 0' }}>
          The customer was told their order is being processed. It goes to Gelato automatically once{' '}
          {h.awaitingApproval && h.awaitingFunds
            ? "it's approved and the payment clears"
            : h.awaitingApproval
              ? "it's approved"
              : 'the payment clears'}
          .
        </p>
      )}
      {h.held && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {h.awaitingApproval && (
            <button type="button" onClick={() => run('approve', 'approve')} disabled={!!busy} style={btnPrimary}>
              {busy === 'approve' ? 'Approving…' : 'Approve order'}
            </button>
          )}
          {order.funds_required === 1 && (
            <button type="button" onClick={() => run('funds', 'funds')} disabled={!!busy} style={btnSecondary}>
              {busy === 'funds' ? 'Checking…' : 'Check Stripe now'}
            </button>
          )}
          {h.awaitingFunds && !h.awaitingApproval && (
            <button
              type="button"
              onClick={() =>
                run(
                  'release',
                  'release-early',
                  "Send to Gelato now without waiting for the payment to clear? If the payment fails or is reversed, you'll still pay Gelato for the print."
                )
              }
              disabled={!!busy}
              style={btnSecondary}
            >
              {busy === 'release' ? 'Sending…' : "Send now (don't wait for funds)"}
            </button>
          )}
        </div>
      )}
      {message && (
        <p style={{ fontSize: 12, color: message.error ? '#b91c1c' : '#15803d', margin: '8px 0 0' }}>{message.text}</p>
      )}
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '6px 12px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '6px 12px',
  background: '#fff',
  color: '#1a1a1a',
  border: '1px solid #d4d4d4',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
};
